import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Castle, Compass } from "lucide-react";
import { API, api } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";
import { PackageCard } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const HERO = "https://tour-operator-demo.emergent.host/img/amalfi.jpg";
// Copertina Discovery Rome: fotografia reale del Colosseo servita dal nostro
// backend (cache permanente object storage, fonte Google Places) — l'asset
// colosseo.jpg sul gestionale è corrotto (placeholder da 3KB).
const ROME_COVER = `${API}/public/cities/roma/attractions/colosseo/photo`;

export default function BuilderHome() {
    const { lang, t } = useLang();
    const navigate = useNavigate();
    const [packages, setPackages] = useState([]);

    useEffect(() => {
        api.get("/public/packages").then((r) => setPackages((r.data || []).slice(0, 6))).catch(() => {});
    }, []);

    return (
        <div data-testid="builder-home" className="marble-bg">
            <Seo lang={lang} title={`Italy. Your Way. — TOURVOYAGERS`} description={t.builder.heroSub} />

            <section className="relative min-h-[78vh] flex items-center overflow-hidden">
                <img src={HERO} alt="Costiera Amalfitana" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#080909]/92 via-[#080909]/55 to-[#080909]/30" />
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#080909] to-transparent" />
                <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-32 w-full">
                    <Reveal>
                        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight">
                            <span className="text-amber-50">{t.builder.heroTitleA}</span>{" "}
                            <span className="italic gold-text">{t.builder.heroTitleB}</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg text-stone-300 leading-relaxed">{t.builder.heroSub}</p>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <button data-testid="builder-cta-create" onClick={() => document.getElementById("choose-mode")?.scrollIntoView({ behavior: "smooth" })}
                                className="inline-flex items-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 text-xs font-bold uppercase tracking-[0.16em] text-black transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_30px_rgba(241,211,108,0.45)]">
                                {t.builder.ctaCreate} <ArrowRight size={15} />
                            </button>
                            <button data-testid="builder-cta-tours" onClick={() => document.getElementById("signature-tours")?.scrollIntoView({ behavior: "smooth" })}
                                className="rounded-full border border-amber-500/60 px-8 py-4 text-xs font-bold uppercase tracking-[0.16em] text-amber-200 transition-colors duration-300 hover:bg-amber-400/10">
                                {t.builder.ctaTours}
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* STEPS */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {t.builder.steps.map((s, i) => (
                        <Reveal key={s.t} delay={i * 0.08}>
                            <div className="flex gap-4">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-500/45 font-mono-accent text-sm text-amber-300">{i + 1}</span>
                                <div>
                                    <p className="text-sm font-semibold text-amber-50">{s.t}</p>
                                    <p className="mt-1 text-xs text-stone-500">{s.d}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* MODE CHOICE */}
            <section id="choose-mode" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="choose-mode-section">
                <Reveal>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-amber-50">{t.builder.chooseTitle}</h2>
                    <p className="mt-3 max-w-xl text-sm text-stone-400">{t.builder.chooseSub}</p>
                </Reveal>
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    {[
                        { mode: "roma", icon: Castle, title: t.builder.romeCard, sub: t.builder.romeSub, img: ROME_COVER, testid: "mode-card-rome" },
                        { mode: "italia", icon: Compass, title: t.builder.italyCard, sub: t.builder.italySub, img: "https://tour-operator-demo.emergent.host/img/venice.jpg", testid: "mode-card-italy" },
                    ].map((m, i) => {
                        const Icon = m.icon;
                        return (
                            <Reveal key={m.mode} delay={i * 0.12}>
                                <button onClick={() => navigate(`/itinerario/${m.mode}`)} data-testid={m.testid}
                                    className="group gold-card relative block w-full overflow-hidden rounded-2xl text-left">
                                    <div className="relative h-56 overflow-hidden">
                                        <GoldImage src={m.img} alt={m.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/85 to-transparent" />
                                        <span className="absolute left-5 bottom-5 flex h-12 w-12 items-center justify-center rounded-full border border-amber-400/60 bg-black/50 text-amber-300 backdrop-blur"><Icon size={20} /></span>
                                    </div>
                                    <div className="p-7">
                                        <h3 className="font-display text-2xl sm:text-3xl text-amber-50">{m.title}</h3>
                                        <p className="mt-2 text-sm text-stone-400 leading-relaxed">{m.sub}</p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-400">
                                            {t.builder.start} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                                        </span>
                                    </div>
                                </button>
                            </Reveal>
                        );
                    })}
                </div>
                <Reveal delay={0.15}>
                    <div className="mt-6 rounded-xl border border-amber-500/15 bg-[#101011] p-5">
                        <p className="text-xs text-stone-400">{t.builder.examples.join("   ·   ")}</p>
                        <p className="mt-1 text-[10px] text-stone-600">{t.builder.examplesNote}</p>
                    </div>
                </Reveal>
            </section>

            {/* SIGNATURE TOURS — catalogo reale */}
            <section id="signature-tours" className="border-t border-amber-500/12 bg-[#0b0b0c]" data-testid="signature-tours-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
                    <Reveal>
                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.3em] text-amber-500 font-semibold">TOURVOYAGERS</p>
                                <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-amber-50">{t.builder.signatureTitle}</h2>
                                <p className="mt-3 max-w-xl text-sm text-stone-400">{t.builder.signatureSub}</p>
                            </div>
                            <Link to="/esperienze" data-testid="signature-view-all" className="group inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-400 hover:text-amber-300">
                                {t.home.viewAll} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="mt-12 grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
                        {packages.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
                    </div>
                </div>
            </section>
        </div>
    );
}
